import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Home as HomeIcon } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await signup(email, pw, name);
      toast.success("Account created");
      nav("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signup failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="hidden md:block relative overflow-hidden bg-[var(--app-black)] text-white">
        <div className="absolute inset-0 grain" />
        <div className="relative h-full flex flex-col justify-between p-10">
          <div className="flex items-center gap-2.5">
            <span className="inline-grid place-items-center w-10 h-10 rounded-full bg-white text-[var(--app-black)]">
              <HomeIcon size={18} strokeWidth={2.4} />
            </span>
            <div className="font-display text-xl">FlipPilot</div>
          </div>
          <div>
            <h1 className="font-display text-5xl lg:text-6xl leading-[0.95]">
              Start closing
              <br />
              <span style={{ color: "var(--app-accent-2)" }}>real cash deals.</span>
            </h1>
            <p className="mt-6 text-white/70 max-w-md leading-relaxed">
              The instant offer engine that turns a raw address into a signed
              purchase agreement — MAO, seller motivation, and AI analysis
              included.
            </p>
          </div>
          <div className="text-xs text-white/50">© 2026 FlipPilot</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <form
          onSubmit={submit}
          data-testid="signup-form"
          className="w-full max-w-sm space-y-6"
        >
          <div>
            <span className="app-eyebrow">/ Free beta</span>
            <h2 className="font-display text-4xl mt-2">Create account</h2>
            <p className="text-[var(--app-text-muted)] text-sm mt-2">
              No card required while in beta.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Full name</Label>
            <Input
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="field-sharp h-11"
              data-testid="signup-name"
              placeholder="Jane Investor"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="field-sharp h-11"
              data-testid="signup-email"
              placeholder="you@company.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pw">Password</Label>
            <Input
              id="pw"
              type="password"
              required
              minLength={6}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="field-sharp h-11"
              data-testid="signup-password"
              placeholder="At least 6 characters"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            data-testid="signup-submit"
            className="app-btn app-btn-primary w-full h-11"
          >
            {busy ? "Creating…" : "Create account"}
          </button>
          <div className="text-sm text-[var(--app-text-muted)] text-center">
            Have an account?{" "}
            <Link
              to="/login"
              className="text-[var(--app-accent-dark)] hover:text-[var(--app-accent)] font-medium"
              data-testid="link-login"
            >
              Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
